import { Box, Button } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { cloneDeep, isEmpty } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Flex from "../../../../../../components/Flex";
import ReadOnlyFields from "../../../../../../components/ReadOnlyFields";
import { PROVIDER_PATHS } from "../../../../../../constants/paths";
import useDisclosure from "../../../../../../hooks/use-disclosure";
import { useQueryParams } from "../../../../../../hooks/use-query-param";
import { useSnackbar } from "../../../../../../hooks/useSnackbar";
import { useAddClaims } from "../../../../../../queries/billings/claims/use-add-claims";
import { useAddElectronicClaim } from "../../../../../../queries/billings/claims/use-add-electronic-claim";
import { useConfiguredClearinghouses } from "../../../../../../queries/billings/claim-integration/use-configured-clearinghouses";
import { useUpdateClaims } from "../../../../../../queries/billings/claims/use-update-claims";
import { useUpdateClaimStatus } from "../../../../../../queries/billings/claims/use-update-claim-status";
import {
  ClaimRequest,
  ClaimRequestVendorTypeEnum,
} from "../../../../../../services";
import {
  DEFAULT_VALUE_LABEL_LIST,
  getErrorSnackbar,
  getIdFromObj,
  getSuccessSnackbar,
  getUnNecessaryTransformData,
  getXtenantId,
  transformMultiSelectBackEndVal,
  transformMultiSelectFrontEndVal,
} from "../../../../../../utils/utils";
import ViewPaperClaimDrawer from "../ViewPaperClaimDrawer";
import { CLAIM_TAB_PATH } from "../claim-navigation";
import {
  CLAIM_PROCEDURE_MESSAGES,
  CLAIM_REQUIRED_FIELDS_MESSAGE,
  isSelected,
  PROCEDURE_CODE_SECTION_ID,
  RENDERING_PROVIDER_REQUIRED_MESSAGE,
  UPDATE_CLAIM_FORM_TYPE,
  UPDATE_CLAIM_PROCEDURE_INITIAL_STATE,
  UPDATE_CLAIM_PROCEDURE_VALIDATION,
  UPDATE_FORM_INITIAL_VALUES,
  UPDATE_FORM_VALIDATION,
} from "../claims-constants";
import {
  formatChargeAmount,
  toClaimNpi,
  touchAndValidate,
} from "../claims-utils";
import DiagnosisCodeGridForClaim from "./DiagnosisCodeGridForClaim";
import NavSubmitClaimModal, { ClaimSaveModal } from "./NavSubmitClaimModal";
import PatientConditionForClaim from "./PatientConditionForClaim";
import ProcedureCodeGridForClaim from "./ProcedureCodeGridForClaim";
import NavSelectClaimTypeModal from "./NavSelectClaimTypeModal";
import ServiceDetailsClaim from "./ServiceDetailsClaim";
import ProviderDetailsClaim from "./ProviderDetailsClaim";
import ClaimValidationErrorsModal from "./ClaimValidationErrorsModal";
import { useRoleController } from "../../../../../../components/useRoleController";
import { useChangeClaimType } from "../../../../../../queries/billings/claims/use-change-claim-type";
import { useClaimScrubValidation } from "../../../../../../queries/billings/claims/use-claim-scrub-validation";
import { useFormScrollIntoView } from "../../../../../../hooks/use-form-scrollInto-view";
import { useNavigationGuard } from "../../../../../../hooks/use-navigation-guard";
import ConfirmationModal from "../../../../../../components/app/ConfirmationModal";
import Text from "../../../../../../components/Text";

interface UpdateClaimProps {
  patientsData?: any;
  claimData?: any;
  isFetching: boolean;
  isLoading: boolean;
  readOnly?: string;
}

const UpdateClaim = ({
  patientsData,
  claimData,
  isFetching,
  isLoading,
  readOnly,
}: UpdateClaimProps) => {
  const { hasUpdate: hasUpdateClaims } = useRoleController("Generate Claims");

  // Which button started the current save, so the save's response handler knows
  // whether to continue into the submit flow. It is never rendered, and it is
  // read from inside formik's submit callback — a state value would be read off
  // the closure that existed before the click, so the SUBMITTED intent could be
  // missed on the first click and only picked up on a second one.
  const claimStatusRef = useRef<string | undefined>(undefined);
  const setClaimStatus = (status: string) => {
    claimStatusRef.current = status;
  };
  // const [validationPassed,setValidationPassed] = useState(false);
  const isCopyClaim = useQueryParams("claimStatus") === "COPY";
  const superBillId = useQueryParams("superBillId");
  const navigate = useNavigate();
  const location = useLocation();

  const { mutateAsync: updateClaimStatus } = useUpdateClaimStatus();

  // When the claim flow was launched from Patient Charting → Financial, the
  // origin table path is passed in router state; on complete/cancel we return
  // there instead of the Billing Claims tab.
  const fromPatientChart = (location.state as any)?.from;
  const formik = useFormik<UPDATE_CLAIM_FORM_TYPE>({
    initialValues: {
      ...UPDATE_FORM_INITIAL_VALUES,
      ...claimData,
    },
    validationSchema: UPDATE_FORM_VALIDATION,
    onSubmit,
    enableReinitialize: true,
  });

  useFormScrollIntoView(formik);

  // Leaving the claim form with unsaved changes should offer to save the claim
  // as Draft (so the Edit Claim option appears) instead of leaving it as-is.
  // Block every route change and ask; explicit save/submit navigations bypass
  // the guard via navigateWithoutGuard, and a completed save skips it too.
  const draftConfirmDisc = useDisclosure(false);
  const hasSavedRef = useRef(false);
  const { proceed: proceedNavigation, navigateWithoutGuard } =
    useNavigationGuard({
      // Already saved/submitted, or read-only view → nothing to draft-save.
      hasByPassGuard: readOnly === "true",
      onNavigationAttempt: () => {
        draftConfirmDisc.open();
      },
    });

  const { mutateAsync: changeClaimTypeAPI } = useChangeClaimType();

  // The claim that the follow-up claim-type / claim-status calls must target.
  // Saving from a Super Bill or a Copy Claim creates a brand-new claim and
  // returns its uuid, while the form is still initialised from the source
  // record (whose uuid is not the claim's) — so remember what the save actually
  // persisted rather than reading the uuid back off the form.
  const savedClaimIdRef = useRef<string | undefined>(undefined);
  const getActiveClaimId = (): string =>
    savedClaimIdRef.current ?? formik.values.uuid ?? "";

  // When only one clearinghouse is configured, submit directly to it and skip
  // the vendor-selection screen.
  const { singleVendor } = useConfiguredClearinghouses();

  const handleChangeClaimTypeSubmit = async (v: any) => {
    const claimId = getActiveClaimId();
    if (!claimId || !v) return;

    // A type was chosen, so this modal is done — close it before opening the
    // paper drawer or the vendor picker, otherwise it stays mounted underneath.
    navSubmitDisc.close();

    if (v === "paper_claim") {
      // Paper claims never reach a clearinghouse — mark the claim SUBMITTED
      // ourselves, then show the printable CMS 1500 form.
      // The claim-type and claim-status endpoints both rewrite the same claim
      // record, so they must run in sequence: firing them together lets the
      // type update (which persists the claim as it read it, still DRAFT) land
      // after the status update and silently revert the claim to Draft.
      try {
        await changeClaimTypeAPI({ claimId, claimType: v });
        const res = await updateClaimStatus({
          claimId,
          status: "SUBMITTED",
          xTENANTID: getXtenantId(),
        });
        showSnackbar(getSuccessSnackbar(res));
        queryClient.invalidateQueries({ queryKey: ["get-claims-list-v2"] });
        queryClient.invalidateQueries({
          queryKey: ["Get-claim-count"],
          refetchType: "all",
        });
      } catch (err) {
        showSnackbar(getErrorSnackbar(err));
        return;
      }
      // Pass the claim UUID to ViewPaperClaimDrawer, it will fetch the full claim data
      viewDisclosure.open(claimId);
    } else if (v === "electronic_claim") {
      try {
        await changeClaimTypeAPI({ claimId, claimType: v });
      } catch (err) {
        showSnackbar(getErrorSnackbar(err));
        return;
      }
      if (singleVendor) {
        handleElectronicClaim({ claimType: singleVendor, claimUuid: claimId });
      } else {
        navSelectClaimTypeDisc.open();
      }
    }
  };

  const [diagnisisCodes, setDiagnisisCodes] = useState<any>(undefined);
  const queryClient = useQueryClient();
  const viewDisclosure = useDisclosure(false, {
    onClose: () =>
      navigateWithoutGuard(() => navigate(fromPatientChart ?? CLAIM_TAB_PATH)),
  });

  const saveDisc = useDisclosure(false);

  // Dismissing the claim-type modals must be a plain close — the user stays on
  // the claim form so they can pick a type instead of being bounced to the list.
  const navSubmitDisc = useDisclosure(false);
  const navSelectClaimTypeDisc = useDisclosure(false);
  const validationErrorsDisc = useDisclosure(false);
  const procedureCodeFormik = useFormik<
    typeof UPDATE_CLAIM_PROCEDURE_INITIAL_STATE
  >({
    initialValues: [],
    // Per-line mandatory checks (diagnosis pointer, quantity, charges, duplicate
    // modifiers) so each failure lands on the field that owns it and the grid can
    // render it inline.
    validationSchema: UPDATE_CLAIM_PROCEDURE_VALIDATION,
    onSubmit: () => {},
    enableReinitialize: true,
  });

  const showSnackbar = useSnackbar();
  const dateOfService = useQueryParams("dateOfService");
  const { mutateAsync: updateClaims } = useUpdateClaims();
  const { mutateAsync: addClaims } = useAddClaims();
  const { mutateAsync: validateClaim } = useClaimScrubValidation();

  const procedureCodeTotal: number = useMemo(
    () =>
      procedureCodeFormik.values.reduce(
        (acc: number, curr: any) => acc + (parseFloat(curr.total as any) || 0),
        0
      ),
    [procedureCodeFormik.values]
  );

  useEffect(() => {
    // this code is for diagnosis code pre-populate
    if (claimData?.diagnosisCodes) {
      setDiagnisisCodes(claimData?.diagnosisCodes);
    }
    // this function will convert the modifiers to populate data to input fileds.
    const transformProcedureList = (list: any) => {
      return list?.map((item: any) => ({
        ...item,
        charges: formatChargeAmount(item.charges),
        modifiers: isEmpty(item.modifiers)
          ? []
          : transformMultiSelectFrontEndVal(
              DEFAULT_VALUE_LABEL_LIST,
              item.modifiers
            ),
      }));
    };
    const transformedProcedureData = transformProcedureList(
      claimData?.patientProcedureCodes?.length > 0
        ? claimData?.patientProcedureCodes
        : undefined
    );
    if (transformedProcedureData) {
      procedureCodeFormik.setValues(transformedProcedureData);
    }
    // The CPT rows are only (re)seeded when the claim itself arrives; depending on
    // the formik instance would re-run this on every keystroke and wipe edits.
  }, [claimData]);

  // Common function to transform claim payload for both save and validation
  const transformClaimPayload = (formikValues: any) => {
    const copiedValues: any = cloneDeep(formikValues);

    const patientProcedurePostParams: any[] = procedureCodeFormik?.values?.map(
      (itm: any) => ({
        ...itm,
        modifiers:
          Array.isArray(itm?.modifiers) && itm.modifiers.length > 0
            ? transformMultiSelectBackEndVal(
                itm.modifiers.filter(
                  (mod: any) => mod && Object.keys(mod).length > 0
                )
              )
            : null,
      })
    );

    const payload: any = getUnNecessaryTransformData(["diagnosisCodes"], {
      ...copiedValues,
      patient: {
        [patientsData?.uuid]: `${patientsData?.firstName} ${patientsData?.lastName}`,
      },
      diagnosisCodes: copiedValues?.diagnisisCodeData ?? null,
      dateOfService: dateOfService || copiedValues?.dateOfService || null,
      patientProcedureCodes: patientProcedurePostParams ?? null,
      orderingProvider: copiedValues?.orderingProviderId ?? null,
      billingProvider: copiedValues?.billingProviderId ?? null,
      // Billing Provider = Practice Group (Billing Entity). Send the practice
      // name (tenant identifier); the backend resolves the practice details.
      practiceName: getXtenantId(),
      referringProvider: copiedValues?.referringProviderId ?? null,
      // Provider NPI numbers (ECRH-5217) — map the form fields onto the backend
      // claim keys so the populated/edited NPIs are persisted and transmitted.
      // The Claim record carries these as the numeric `...NPI` keys, which is
      // also what the form reads back on reopen; sending the superBill-style
      // camelCase keys instead left the edit unrecognised, so the claim kept
      // the NPI the backend had resolved from the provider record.
      renderingProviderNPI: toClaimNpi(
        copiedValues?.renderingProviderNPINumber
      ),
      referringProviderNPI: toClaimNpi(
        copiedValues?.referringProviderNPINumber
      ),
      orderingProviderNPI: toClaimNpi(copiedValues?.orderingProviderNPINumber),
      // Billing Provider is the Practice Group and its NPI is resolved by the
      // backend from `practiceName` (ECRH-5422) — the field is display-only, so
      // this key is left exactly as it was rather than promoted like the three
      // editable NPIs above.
      billingProviderNpi: copiedValues?.billingProvideNPINumber || null,
      state: copiedValues?.serviceState ?? null,
      // Place of Service is an enum on the claim, and the form carries "" for
      // "not chosen" - both as its initial value and when a saved claim comes
      // back without one. Sending that empty string makes the backend reject
      // the whole request ("Invalid enum value for the field: 'placeOfService'")
      // which, on Save as Draft, reads to the user as the draft refusing to
      // save. Send nothing at all when there is nothing to send.
      placeOfService: copiedValues?.placeOfService || null,
      provider: copiedValues?.renderingproviderId ?? null,
      serviceLocation: copiedValues?.locationId ?? null,
      // ECRH-5370: resolved Billing Address (selected Service Location, or the
      // Provider Group Profile when none) for the Claim / CMS-1500 Box 32.
      serviceLocationAddress: copiedValues?.serviceLocationAddress ?? null,
      total: procedureCodeTotal,
      partialSave: false,
    });

    return payload;
  };

  function onSubmit(values: any) {
    // Zero-amount guard removed — a claim may be saved/submitted with zero
    // amount (no billable services), including as a draft.
    // if (!(Number(procedureCodeTotal) > 0)) {
    //   showSnackbar({
    //     message:
    //       "Claim cannot be created with zero amount. Please ensure billable services are added.",
    //     type: "error",
    //   });
    //   return;
    // }

    // This runs only from "Save & Submit", so the claim has to be stored as
    // SUBMITTED. The form still carries the status the claim was loaded with —
    // DRAFT for a claim that was just created — and spreading the form values
    // would write that straight back, leaving the claim on Draft.
    const payload = {
      ...transformClaimPayload(values),
      claimStatus: claimStatusRef.current ?? "SUBMITTED",
    };

    // setCmsFormData({
    //   ...formik.values,
    //   patientProcedureCodes: patientProcedurePostParams,
    // });

    const transFormApiPayload = {
      claim: payload,
      xTENANTID: getXtenantId(),
    };

    if (superBillId || isCopyClaim)
      showToastAndReset(
        addClaims(transFormApiPayload as any),
        transFormApiPayload
      );
    else
      showToastAndReset(
        updateClaims(transFormApiPayload as any),
        transFormApiPayload
      );
  }

  const showToastAndReset = (apiCall: Promise<any>, payload?: any) => {
    return apiCall
      ?.then(async (res) => {
        // navigate(PROVIDER_PATHS.BILLING);
        // A save/submit succeeded — stop the draft-save guard from prompting
        // again if the user then navigates away.
        hasSavedRef.current = true;
        // A create returns the uuid of the claim it just stored; an update
        // keeps working on the claim the form was loaded with.
        savedClaimIdRef.current =
          (superBillId || isCopyClaim ? res?.data?.data : undefined) ??
          formik.values.uuid ??
          res?.data?.data;

        showSnackbar(getSuccessSnackbar(res));
        queryClient.invalidateQueries({ queryKey: ["get-claims-list-v2"] });
        queryClient.invalidateQueries({ queryKey: ["appointment-list-v2"] });

        if (claimStatusRef.current === "SUBMITTED") {
          // Check if claim is already paper type - show ViewPaperClaimDrawer directly
          const currentClaimType =
            payload?.claim?.claimType?.toLowerCase() ||
            claimData?.claimType?.toLowerCase();

          if (
            currentClaimType === "paper_claim" ||
            currentClaimType === "paper"
          ) {
            // Claim is already paper type, show ViewPaperClaimDrawer directly
            const claimId = getActiveClaimId();
            if (!claimId) return;

            // Wait for the status to be stored before opening the drawer, so a
            // rejected update surfaces instead of leaving the claim on Draft.
            try {
              await updateClaimStatus({
                claimId,
                status: "SUBMITTED",
                xTENANTID: getXtenantId(),
              });
              queryClient.invalidateQueries({
                queryKey: ["get-claims-list-v2"],
              });
              queryClient.invalidateQueries({
                queryKey: ["Get-claim-count"],
                refetchType: "all",
              });
            } catch (err) {
              showSnackbar(getErrorSnackbar(err));
              return;
            }
            viewDisclosure.open(claimId);
          } else {
            // Show modal to select claim type
            navSubmitDisc.open(payload?.claim);
          }
        }
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  };

  const { mutateAsync: addElectronicClaim } = useAddElectronicClaim();
  function handleElectronicClaim({
    claimType,
    claimUuid,
  }: {
    claimType: ClaimRequestVendorTypeEnum;
    claimUuid: string;
  }) {
    if (!claimUuid) {
      showSnackbar({
        message: "Claim ID is required to submit electronic claim",
        type: "error",
      });
      return;
    }
    const electronicClaimParams: ClaimRequest = {
      claimUuids: [claimUuid ?? ""],
      vendorType: claimType,
    };
    showElectronicClaimToastAndNavigate(
      addElectronicClaim({ claim: electronicClaimParams })
    );
  }

  const showElectronicClaimToastAndNavigate = (apiCall: Promise<any>) => {
    return apiCall
      ?.then((res) => {
        navSelectClaimTypeDisc.close();
        showSnackbar(getSuccessSnackbar(res));
        queryClient.invalidateQueries({ queryKey: ["get-claims-list-v2"] });
        queryClient.invalidateQueries({ queryKey: ["appointment-list-v2"] });
        navigateWithoutGuard(() =>
          navigate(fromPatientChart ?? CLAIM_TAB_PATH)
        );
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  };

  const showRequiredFieldsError = () =>
    showSnackbar({ message: CLAIM_REQUIRED_FIELDS_MESSAGE, type: "error" });

  // Shared validation gate used by both the "Save" and "Save & Submit" buttons:
  // the per-CPT-line procedure checks (diagnosis pointer, duplicate modifiers,
  // quantity, charges) plus the formik required-field schema. Every field is
  // marked touched first, so each missing value is flagged in place — asterisk,
  // red border and its own message — and the snackbar only carries the generic
  // prompt instead of a concatenated list of every failure.
  const passesSaveSubmitValidation = async (): Promise<boolean> => {
    // A claim with no CPT line at all is a different problem from a blank field:
    // there is no field to flag, so it keeps its own message.
    if (!procedureCodeFormik.values?.length) {
      showSnackbar({
        message: CLAIM_PROCEDURE_MESSAGES.CPT_CODE_REQUIRED,
        type: "error",
      });
      return false;
    }

    const [formErrors, procedureErrors] = await Promise.all([
      touchAndValidate(formik),
      touchAndValidate(procedureCodeFormik),
    ]);

    if (isEmpty(formErrors) && isEmpty(procedureErrors)) return true;

    showRequiredFieldsError();

    // The CPT lines sit below the fold, so a line-level failure is invisible
    // without bringing the section into view. Form-level errors are handled by
    // useFormScrollIntoView.
    if (isEmpty(formErrors)) {
      document
        .getElementById(PROCEDURE_CODE_SECTION_ID)
        ?.scrollIntoView({ block: "center", behavior: "smooth" });
    }
    return false;
  };

  // Handler for Validate Claim button
  const handleValidateClaim = async () => {
    const errors = await touchAndValidate(formik);
    if (!isEmpty(errors)) {
      showRequiredFieldsError();
      return;
    }

    // Use the common transformation function
    const payload = transformClaimPayload(formik.values);

    const transFormApiPayload = {
      claim: payload,
      xTENANTID: getXtenantId(),
    };

    validateClaim(transFormApiPayload)
      .then((res: any) => {
        if (res?.data?.data?.passed) {
          // setValidationPassed(true);
          showSnackbar({
            message: "Claim scrubbed successfully. No errors found.",
            type: "success",
          });
        }
      })
      .catch((err: any) => {
        const errorData = err?.response?.data?.data?.errors;
        const passed = err?.response?.data?.data?.passed;
        // setValidationPassed(!!passed);
        if (passed) {
          showSnackbar(getErrorSnackbar(err));
        } else {
          validationErrorsDisc.open(errorData);
        }
      });
  };

  // Persist whatever the user has entered as a DRAFT, bypassing the mandatory
  // field checks apart from Rendering Provider — the claim is unusable without
  // it. The rest (NPI, CPT lines, service location…) are allowed to be missing
  // on a draft and are only enforced on Save / Save & Submit. `partialSave`
  // tells the backend to skip its own mandatory validation for the same reason.
  // Resolves to true only when the claim was actually stored, so the caller can
  // keep the user on the form when the save failed.
  const saveClaimAsDraft = async (): Promise<boolean> => {
    if (!isSelected(formik.values.renderingproviderId)) {
      // Flag the field too, so the error shows where it has to be fixed.
      formik.setFieldTouched("renderingproviderId", true);
      showSnackbar({
        message: RENDERING_PROVIDER_REQUIRED_MESSAGE,
        type: "error",
      });
      return false;
    }

    const payload = {
      ...transformClaimPayload(formik.values),
      claimStatus: "DRAFT",
      partialSave: true,
    };
    const transFormApiPayload = { claim: payload, xTENANTID: getXtenantId() };
    const apiCall =
      superBillId || isCopyClaim
        ? addClaims(transFormApiPayload as any)
        : updateClaims(transFormApiPayload as any);

    return apiCall
      .then((res) => {
        // Saved — the navigation guard no longer needs to offer a draft save.
        hasSavedRef.current = true;
        showSnackbar(getSuccessSnackbar(res));
        queryClient.invalidateQueries({ queryKey: ["get-claims-list-v2"] });
        queryClient.invalidateQueries({ queryKey: ["appointment-list-v2"] });
        queryClient.invalidateQueries({ queryKey: ["Get-claim-count"] });
        return true;
      })
      .catch((err) => {
        showSnackbar(getErrorSnackbar(err));
        return false;
      });
  };

  // "Yes" → save as draft, then continue to where the user was navigating.
  // Stay on the form when the draft could not be stored (a missing Rendering
  // Provider, or a failed call), otherwise the entered claim is lost silently.
  const handleConfirmSaveDraft = async () => {
    setClaimStatus("DRAFT");
    draftConfirmDisc.close();
    if (!(await saveClaimAsDraft())) return;
    proceedNavigation();
  };

  // "No" → don't save, just go where the user clicked.
  const handleCancelSaveDraft = () => {
    draftConfirmDisc.close();
    proceedNavigation();
  };

  // "Save" → the claim type chosen in ClaimSaveModal is applied to the claim and
  // the claim is persisted as READY_FOR_SUBMIT, then we return to the claims list.
  const handleSaveClaim = async (claimType: any) => {
    const claimId = formik.values.uuid ?? "";

    if (!claimType) {
      showSnackbar({
        message: "Please select a claim type",
        type: "error",
      });
      return;
    }

    // Each CPT line needs an ICD pointer, a quantity and a charge, and the
    // mandatory form fields must be filled, before the claim can move to Ready
    // to Submit. The gate flags every offending field in place.
    if (!(await passesSaveSubmitValidation())) {
      saveDisc.close();
      return;
    }

    setClaimStatus("READY_FOR_SUBMIT");

    const payload = {
      ...transformClaimPayload(formik.values),
      claimType,
      claimStatus: "READY_FOR_SUBMIT",
    };
    const transFormApiPayload = { claim: payload, xTENANTID: getXtenantId() };

    try {
      if (claimId) await changeClaimTypeAPI({ claimId, claimType });

      const res = await (superBillId || isCopyClaim
        ? addClaims(transFormApiPayload as any)
        : updateClaims(transFormApiPayload as any));

      hasSavedRef.current = true;
      showSnackbar(getSuccessSnackbar(res));
      queryClient.invalidateQueries({ queryKey: ["get-claims-list-v2"] });
      queryClient.invalidateQueries({ queryKey: ["appointment-list-v2"] });
      // The status cards live on the claims list, which is unmounted while the
      // claim form is open — refetch the counts even though the query is
      // inactive, so the sub-tab counts are already fresh when we land back.
      queryClient.invalidateQueries({
        queryKey: ["Get-claim-count"],
        refetchType: "all",
      });
      saveDisc.close();
      navigateWithoutGuard(() =>
        navigate(fromPatientChart ?? PROVIDER_PATHS.CLAIMS)
      );
    } catch (err) {
      showSnackbar(getErrorSnackbar(err));
    }
  };

  return (
    <Flex width={"100%"} overflow={"scroll"} flexDirection={"column"}>
      <ConfirmationModal
        maxWidth="sm"
        disclosureHost={{ ...draftConfirmDisc, close: handleCancelSaveDraft }}
        onConfirm={handleConfirmSaveDraft}
        confirmationText="Yes"
        cancelText="No"
        title={
          <Text fontWeight={"medium"} fontSize={"20px"}>
            Do you want to save this changes ?
          </Text>
        }
      />
      <ReadOnlyFields active={readOnly === "true"}>
        <Flex gap={1} flexDirection={"column"}>
          <ServiceDetailsClaim formik={formik} readOnly={readOnly === "true"} />
          <ProviderDetailsClaim
            formik={formik}
            readOnly={readOnly === "true"}
          />
          <DiagnosisCodeGridForClaim
            formik={formik}
            diagnosisCodes={diagnisisCodes}
            isFetching={isFetching}
            isLoading={isLoading}
            readOnly={readOnly}
          />
          <ProcedureCodeGridForClaim
            diagnosisCodesLength={
              formik?.values?.diagnisisCodeData?.length ?? 0
            }
            procedureCodeFormik={procedureCodeFormik as any}
            readOnly={readOnly}
            renderingProviderUuid={getIdFromObj(
              formik.values.renderingproviderId
            )}
            payerId={formik.values.patientInsurance?.insurancePayer?.payerId}
            procedureCodeTotal={procedureCodeTotal}
          />
          <PatientConditionForClaim formik={formik} readOnly={readOnly} />
        </Flex>
      </ReadOnlyFields>
      {readOnly !== "true" && (
        <Box
          sx={{
            marginLeft: "auto",
            padding: "10px",
          }}
        >
          {/* on save claim */}
          <ClaimSaveModal disclosureHost={saveDisc} onSave={handleSaveClaim} />

          <NavSubmitClaimModal
            disclosureHost={navSubmitDisc}
            onNextClick={handleChangeClaimTypeSubmit}
          />
          <NavSelectClaimTypeModal
            disclosureHost={navSelectClaimTypeDisc}
            onNextClick={(v) => {
              if (v === "WAYSTAR") {
                handleElectronicClaim({
                  claimType: ClaimRequestVendorTypeEnum.Waystar,
                  claimUuid: getActiveClaimId(),
                });
              } else if (v === "CLAIM_MD") {
                handleElectronicClaim({
                  claimType: ClaimRequestVendorTypeEnum.ClaimMd,
                  claimUuid: getActiveClaimId(),
                });
              } else if (v === "STEDI") {
                handleElectronicClaim({
                  claimType: ClaimRequestVendorTypeEnum.Stedi,
                  claimUuid: getActiveClaimId(),
                });
              }
            }}
          />
          <ViewPaperClaimDrawer disclosureHost={viewDisclosure} />
          <ClaimValidationErrorsModal
            disclosureHost={validationErrorsDisc}
            errors={validationErrorsDisc.data}
          />

          {hasUpdateClaims && (
            <Box
              sx={{
                display: "flex",
                gap: "5px",
              }}
            >
              <Button variant="text" onClick={handleValidateClaim}>
                Validate Claim
              </Button>
              <Button
                // disabled={!validationPassed}
                onClick={async () => {
                  // A draft must keep whatever has been entered so far, so no
                  // required-field validation runs here — going through
                  // formik.submitForm() would abort on the schema errors and
                  // silently discard the user's input.
                  setClaimStatus("DRAFT");
                  // `false` → don't re-run the schema, nothing here is gated on it.
                  await formik.setFieldValue("claimStatus", "DRAFT", false);
                  const saved = await saveClaimAsDraft();
                  if (saved) {
                    // localStorage.setItem("billingSelectedTab", "claims");
                    navigateWithoutGuard(() =>
                      navigate(fromPatientChart ?? PROVIDER_PATHS.CLAIMS)
                    );
                  }
                }}
                variant="outlined"
              >
                Save as Draft
              </Button>
              <Button
                onClick={async () => {
                  // Run the same validation as Save & Submit up front, so an
                  // invalid claim never opens the claim-type selection modal.
                  if (!(await passesSaveSubmitValidation())) return;
                  saveDisc.open(transformClaimPayload(formik?.values));
                }}
                variant="outlined"
              >
                Save
              </Button>
              <Button
                // disabled={!validationPassed}
                onClick={async () => {
                  // ECRH-5483: block clearinghouse submission until every CPT
                  // line has an ICD pointer, a modifier and a charge amount, and
                  // the required form fields are filled.
                  if (!(await passesSaveSubmitValidation())) return;
                  // console.log(
                  //   "val here check for the claim status ",
                  //   formik.values?.claimStatus,
                  //   claimStatus
                  // );
                  // return;

                  setClaimStatus("SUBMITTED");
                  // await formik.setFieldValue("claimStatus", "DRAFT");
                  await formik.submitForm();
                  queryClient.invalidateQueries({
                    queryKey: ["Get-claim-count"],
                  });
                }}
                variant="contained"
              >
                Save & Submit
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Flex>
  );
};

export default UpdateClaim;
